import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, finalize, map } from 'rxjs/operators';
import {
  DatosMinimosEmpleado,
  DiaConTrabajo,
  Turnos,
  TurnosVariosEmpleados,
} from '../../models/personal/turnos.model';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../../models/base';
import { MatSnackBar } from '@angular/material/snack-bar'; 
import { BaseService } from '../base';
import { CalendarEvent } from 'angular-calendar';
import moment from 'moment';
import { VacacionesService } from './vacaciones.service';
import { Vacaciones } from '../../models/personal/vacaciones.model';
import { EmployeesListModel } from '../../modules/employees/employees-list.model';
import { BajasService } from './bajas.service';
import { Bajas } from '../../models/personal/bajas.models';
import { AsuntosPropiosService } from './asuntos-propios.service';
import { AsuntosPropios } from '../../models/personal/asuntos-propios.models';

const TURNOS_ENDPOINT = '/turnos/';
const EMPLEADOS_ENDPOINT = '/employees/';

@Injectable({
  providedIn: 'root',
})
export class TurnosService extends BaseService {
  empleados: DatosMinimosEmpleado[] = [];
  e$: BehaviorSubject<DatosMinimosEmpleado[]> = new BehaviorSubject<DatosMinimosEmpleado[]>([]);
  empleados$: Observable<DatosMinimosEmpleado[]> = this.e$.asObservable();

  listaEmpleados: EmployeesListModel[] = [];
  le$: BehaviorSubject<EmployeesListModel[]> = new BehaviorSubject<EmployeesListModel[]>([]);
  listaEmpleados$: Observable<EmployeesListModel[]> = this.le$.asObservable();

  listaTurnos: Turnos[] = null;
  lt$: BehaviorSubject<Turnos[]> = new BehaviorSubject<Turnos[]>([]);
  listaTurnos$: Observable<Turnos[]> = this.lt$.asObservable();

  listaDiasTrabajo: CalendarEvent[] = [];
  ldt$: BehaviorSubject<CalendarEvent[]> = new BehaviorSubject<CalendarEvent[]>([]);
  listaDiasTrabajo$: Observable<CalendarEvent[]> = this.ldt$.asObservable();

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    protected snackBar: MatSnackBar,
    private asuntosPropiosService: AsuntosPropiosService,
    private vacacionesService: VacacionesService,
    private bajasService: BajasService
  ) {
    super(snackBar);
  }

  deleteTurno(id: number): Observable<boolean> {
    this.loading$.next(true);

    return this.http
      .delete<ApiResponse<boolean>>(environment.apiUrl + TURNOS_ENDPOINT + id)
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            this.listaTurnos = (this.listaTurnos || []).filter((t) => t.id != id);
            this.lt$.next(this.listaTurnos);

            this.snackBar.open('Turno correctamente eliminado', undefined, {
              duration: 3000,
            });

            return true;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Método para obtener la lista de empleados
   * @returns Observable de la lista de empleados
   */
  obtenerListaEmpleados(): Observable<EmployeesListModel[]> {
    if (this.listaEmpleados.length == 0) {
      return this.http.get<ApiResponse<EmployeesListModel[]>>(environment.apiUrl + EMPLEADOS_ENDPOINT).pipe(
        map((resultado) => {
          this.listaEmpleados = resultado.data;
          this.le$.next(this.listaEmpleados);
          return this.listaEmpleados;
        })
      );
    } else {
      return this.le$.asObservable();
    }
  }

  /**
   * Método para obtener la lista de días con trabajo en un rango de fechas.
   * @param fechaInicio Fecha de inicio del rango. Debe estar en formato DD-MM-YYYY
   * @param fechaFin Fecha de fin del rango. Debe estar en formato DD-MM-YYYY
   */
  getListaDiasTrabajo(fechaInicio: string, fechaFin: string): void {
    this.loading$.next(true);

    this.listaDiasTrabajo = [];
    this.empleados = [];

    this.http
      .get<ApiResponse<DiaConTrabajo[]>>(
        environment.apiUrl +
          TURNOS_ENDPOINT +
          'listaDiasConTrabajo/' +
          fechaInicio +
          '/' +
          fechaFin
      )
      .pipe(
        concatMap((resultado) => {
          if (resultado && resultado.data) {
            resultado.data.forEach((dia) => {
              dia.empleados.forEach((empleado) => {
                const color = {
                  primary: empleado.color.primary,
                  secondary: empleado.color.secondary,
                };

                this.listaDiasTrabajo.push({
                  start: moment(dia.cadenaFecha, 'DD/MM/YYYY').toDate(),
                  title:
                    empleado.nombre +
                    ' - ' +
                    empleado.nombreTurno +
                    ' - ' +
                    empleado.horas,
                  color: color,
                  id: empleado.employees_Id,
                  meta: {
                    vacaciones: false,
                  },
                  cssClass: 'evento-indicador',
                });
              });
            });

            resultado.data.forEach((dia) => {
              dia.empleados.forEach((empleado) => {
                if (
                  !this.empleados.find(
                    (e) => e.employees_Id == empleado.employees_Id
                  )
                ) {
                  this.empleados.push(empleado);
                }
              });
            });

            return this.obtenerListaEmpleados();
          } else if (resultado && resultado.error) {
            this.procesarError(resultado);
            return of(null);
          }

          return of(null);
        }),
        concatMap(() => {
          return this.asuntosPropiosService.getAsuntosPropiosPorFechas(
            fechaInicio,
            fechaFin
          );
        }),
        concatMap((asuntosPropios: AsuntosPropios[]) => {
          if (asuntosPropios) {
            asuntosPropios.forEach((asuntoPropio) => {
              const empleado = this.listaEmpleados.find(
                (e) => e.uid == asuntoPropio.employees_Id
              );

              const fechaInicioLocal = moment(
                asuntoPropio.cadenaInicio,
                'DD/MM/YYYY'
              );
              const fechaFinLocal = moment(
                asuntoPropio.cadenaFin,
                'DD/MM/YYYY'
              );

              const diasAsuntosPropios = [];
              while (fechaInicioLocal.isSameOrBefore(fechaFinLocal)) {
                diasAsuntosPropios.push(fechaInicioLocal.toDate());
                fechaInicioLocal.add(1, 'days');
              }

              diasAsuntosPropios.forEach((fecha) => {
                this.listaDiasTrabajo.push({
                  start: fecha,
                  title: 'Asuntos propios - ' + (empleado ? empleado.name : ''),
                  color: {
                    primary: '#000000',
                    secondary: '#fff',
                  },
                  id: asuntoPropio.employees_Id,
                  meta: {
                    asuntosPropios: true,
                  },
                  cssClass: 'evento-asuntos-propios',
                });
              });

              if (
                !this.empleados.find(
                  (e) => e.employees_Id == asuntoPropio.employees_Id
                )
              ) {
                this.empleados.push({
                  employees_Id: asuntoPropio.employees_Id,
                  nombre: empleado ? empleado.name : '',
                  nombreTurno: 'Asuntos propios',
                  horas: '0',
                  color: {
                    primary: '#ff0000',
                    secondary: '#ff0000',
                  },
                });
              }
            });
          }

          return of(null);
        }),
        concatMap(() => {
          return this.bajasService.getBajasPorFechas(fechaInicio, fechaFin);
        }),
        concatMap((bajas: Bajas[]) => {
          if (bajas) {
            bajas.forEach((baja) => {
              const empleado = this.listaEmpleados.find(
                (e) => e.uid == baja.employees_Id
              );

              const fechaInicioLocal = moment(baja.cadenaInicio, 'DD/MM/YYYY');
              let fechaFinLocal = moment(fechaFin, 'DD/MM/YYYY');

              if (baja.cadenaFin != 'N/A') {
                fechaFinLocal = moment(baja.cadenaFin, 'DD/MM/YYYY');
              }

              const diasBajas = [];
              while (fechaInicioLocal.isSameOrBefore(fechaFinLocal)) {
                diasBajas.push(fechaInicioLocal.toDate());
                fechaInicioLocal.add(1, 'days');
              }

              diasBajas.forEach((fecha) => {
                this.listaDiasTrabajo.push({
                  start: fecha,
                  title: 'Baja - ' + (empleado ? empleado.name : ''),
                  color: {
                    primary: '#000000',
                    secondary: '#fff',
                  },
                  id: baja.employees_Id,
                  meta: {
                    baja: true,
                  },
                  cssClass: 'evento-baja',
                });
              });

              if (
                !this.empleados.find((e) => e.employees_Id == baja.employees_Id)
              ) {
                this.empleados.push({
                  employees_Id: baja.employees_Id,
                  nombre: empleado ? empleado.name : '',
                  nombreTurno: 'Baja',
                  horas: '0',
                  color: {
                    primary: '#ff0000',
                    secondary: '#ff0000',
                  },
                });
              }
            });
          }

          return of(null);
        }),
        concatMap(() => {
          return this.vacacionesService.getVacacionesPorFechas(
            fechaInicio,
            fechaFin
          );
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.ldt$.next(this.listaDiasTrabajo);
          this.e$.next(this.empleados);
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado: Vacaciones[]) => {
          if (resultado) {
            resultado.forEach((vacacion) => {
              const empleado = this.listaEmpleados.find(
                (e) => e.uid == vacacion.employees_Id
              );

              const fechaInicioLocal = moment(
                vacacion.cadenaInicio,
                'DD/MM/YYYY'
              );
              const fechaFinLocal = moment(vacacion.cadenaFin, 'DD/MM/YYYY');

              const diasVacaciones = [];
              while (fechaInicioLocal.isSameOrBefore(fechaFinLocal)) {
                diasVacaciones.push(fechaInicioLocal.toDate());
                fechaInicioLocal.add(1, 'days');
              }

              diasVacaciones.forEach((fecha) => {
                this.listaDiasTrabajo.push({
                  start: fecha,
                  title: 'Vacaciones - ' + (empleado ? empleado.name : ''),
                  color: {
                    primary: '#000000',
                    secondary: '#fff',
                  },
                  id: vacacion.employees_Id,
                  meta: {
                    vacaciones: true,
                  },
                  cssClass: 'evento-vacaciones',
                });
              });

              if (
                !this.empleados.find(
                  (e) => e.employees_Id == vacacion.employees_Id
                )
              ) {
                this.empleados.push({
                  employees_Id: vacacion.employees_Id,
                  nombre: empleado ? empleado.name : '',
                  nombreTurno: 'Vacaciones',
                  horas: '0',
                  color: {
                    primary: '#fff',
                    secondary: '#fff',
                  },
                });
              }
            });
          }

          this.ldt$.next(this.listaDiasTrabajo);
          this.e$.next(this.empleados);
        },
      });
  }

  /**
   * Método para obtener la lista de turnos en formato Observable y actualizar la lista de turnos.
   * Este método no devuelve valores, ya que el resultado lo asigna al observable de la lista de turnos.
   */
  getListaTurnos(): void {
    this.loading$.next(true);

    this.http
      .get<ApiResponse<Turnos[]>>(environment.apiUrl + TURNOS_ENDPOINT)
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado) => {
          if (resultado && resultado.data) {
            this.listaTurnos = resultado.data;
            this.lt$.next(this.listaTurnos);
          } else if (resultado && resultado.error) {
            this.procesarError(resultado);
          }
        },
      });
  }

  newTurnoVariosEmpleados(): TurnosVariosEmpleados {
    return {
      employees_Ids: [],
      nombre: '',
      descripcion: '',
      creacion: null,
      inicio: null,
      fin: null,
      tipo: null,
      horas: '',
      diasALaSemana: '',
      diasDescanso: '',
      users_id: 0,
      cadenaInicio: '',
      cadenaFin: '',
      listaDiasDescanso: [],
    };
  }

  /**
   * Guarda un turno para varios empleados en la base de datos
   * @param turno Información del turno
   * @returns Observable de la lista de turnos guardados
   */
  guardarTurnoVariosEmpleados(turno: TurnosVariosEmpleados): Observable<Turnos[]> {
    this.loading$.next(true);

    return this.http
      .post<ApiResponse<Turnos[]>>(environment.apiUrl + TURNOS_ENDPOINT + 'variosEmpleados', turno)
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            resultado.data.forEach((turnoGuardado) => {
              if (
                turnoGuardado.id > 0 &&
                this.listaTurnos.findIndex((t) => t.id == turnoGuardado.id) >= 0
              ) {
                const indice = this.listaTurnos.findIndex(
                  (t) => t.id == turnoGuardado.id
                );
                this.listaTurnos[indice] = turnoGuardado;
              } else {
                this.listaTurnos.push(turnoGuardado);
              }
            });

            this.lt$.next(this.listaTurnos);

            this.snackBar.open('Turnos correctamente guardados', undefined, {
              duration: 3000,
            });

            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Guarda un turno en la base de datos
   * @param turno Turno a guardar
   */
  saveTurno(turno: Turnos): Observable<Turnos> {
    this.loading$.next(true);

    const request$ =
      turno.id > 0
        ? this.http.put<ApiResponse<Turnos>>(environment.apiUrl + TURNOS_ENDPOINT, turno)
        : this.http.post<ApiResponse<Turnos>>(environment.apiUrl + TURNOS_ENDPOINT, turno);

    return request$.pipe(
      map((resultado) => {
        if (resultado && resultado.data) {
          if (
            turno.id > 0 &&
            this.listaTurnos.findIndex((t) => t.id == turno.id) >= 0
          ) {
            const indice = this.listaTurnos.findIndex((t) => t.id == turno.id);
            this.listaTurnos[indice] = resultado.data;
          } else {
            this.listaTurnos.push(resultado.data);
          }

          this.lt$.next(this.listaTurnos);

          this.snackBar.open('Turno correctamente guardado', undefined, {
            duration: 3000,
          });

          return resultado.data;
        }

        this.procesarError(resultado);
        throw resultado;
      }),
      catchError((error) => {
        this.procesarError(error && error.error ? error.error : error);
        return throwError(error);
      }),
      finalize(() => {
        this.loading$.next(false);
      })
    );
  }

  // #endregion

  // #region Métodos de gestión internos

  /**
   * Método para comprobar si un empleado tiene vacaciones en la lista de días de trabajo
   * @param empleado Empleado a comprobar
   * @returns True si el empleado tiene vacaciones en el día, false en caso contrario
   */
  empleadoTieneVacaciones(empleado: DatosMinimosEmpleado): boolean {
    if (
      this.listaDiasTrabajo.length > 0 &&
      this.listaDiasTrabajo.find(
        (dia) => dia.id == empleado.employees_Id && dia.meta.vacaciones
      ) != null
    ) {
      return true;
    }

    return false;
  }

  /**
   * Método para comprobar si un empleado tiene baja en la lista de días de trabajo
   * @param empleado Empleado a comprobar
   * @returns True si el empleado tiene baja en el día, false en caso contrario
   */
  empleadoTieneBaja(empleado: DatosMinimosEmpleado): boolean {
    if (
      this.listaDiasTrabajo.length > 0 &&
      this.listaDiasTrabajo.find((dia) => dia.id == empleado.employees_Id && dia.meta.baja) != null
    ) {
      return true;
    }
    return false;
  }

  /**
   * Método para comprobar si un empleado tiene asuntos propios en la lista de días de trabajo
   * @param empleado Empleado a comprobar
   * @returns True si el empleado tiene asuntos propios en el día, false en caso contrario
   */
  empleadoTieneAsuntosPropios(empleado: DatosMinimosEmpleado): boolean {
    if (
      this.listaDiasTrabajo.length > 0 &&
      this.listaDiasTrabajo.find(
        (dia) => dia.id == empleado.employees_Id && dia.meta.asuntosPropios
      ) != null
    ) {
      return true;
    }
    return false;
  }

  // #endregion
}